import React, { useState } from 'react';

import { Control, Controller, useWatch } from 'react-hook-form';

import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { DEPOSIT_TYPE_LABELS, TourOfferBookingTerms, TourOfferPaymentScheduleTemplate } from '~/types/tours/tourOffer';

import NativeTourApplyDepositToBrand from './NativeTourApplyDepositToBrand';
import NativeTourCard from './NativeTourCard';
import NativeTourEntries from './NativeTourEntries';
import NativeTourNumberInput from './NativeTourNumberInput';
import NativeTourTemplateDetails from './NativeTourTemplateDetails';
import { NativeTourForm, depositAmountProblem, templateProblem } from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
  templates: Array<TourOfferPaymentScheduleTemplate> | undefined;
  isLoadingTemplates: boolean;
  tourOfferId: string;
  brandName: string | null;
  hasUnsavedBookingTerms: boolean;
}

type DepositType = NonNullable<TourOfferBookingTerms['depositType']>;

// The deposit types the tour form offers a Salesforce tour; a flat deposit is kept if a tour has one.
const OFFERED_DEPOSIT_TYPES: Array<DepositType> = ['noDeposit', 'default', 'percentage'];

const SUPPLIER_DUE_DAYS_DEFAULT = 90;

function describeRange(template: TourOfferPaymentScheduleTemplate): string {
  const range = template.leadTimeRange;
  if (!range) {
    return 'Any lead time';
  }
  return range.maxDays === null
    ? `Booked ${range.minDays}+ days ahead`
    : `Booked ${range.minDays}–${range.maxDays} days ahead`;
}

/** What customers pay up front, when the rest is due, and the schedule they pay it on. */
export default function NativeTourBookingSection({
  control,
  templates,
  isLoadingTemplates,
  tourOfferId,
  brandName,
  hasUnsavedBookingTerms,
}: Props) {
  const terms = useWatch({ control, name: 'bookingTerms' });
  const depositTypes =
    terms.depositType === 'flat' ? [...OFFERED_DEPOSIT_TYPES, 'flat' as DepositType] : OFFERED_DEPOSIT_TYPES;
  const amountProblem = depositAmountProblem(terms);
  const savedIsLeadTime =
    terms.scheduleTemplateIds.length > 1 ||
    !!templates?.find((t) => t.id === terms.scheduleTemplateIds[0])?.leadTimeRange;
  // Follows what's saved until it's switched, since switching clears the templates it was read from.
  const [pickedMode, setPickedMode] = useState<'single' | 'leadTime' | null>(null);
  const leadTime = (pickedMode ?? (savedIsLeadTime ? 'leadTime' : 'single')) === 'leadTime';
  const problem = templateProblem(terms.scheduleTemplateIds, templates);
  const nameOf = (id: string) => templates?.find((template) => template.id === id)?.name ?? id;

  return (
    <Stack spacing={3}>
      <NativeTourCard title="Deposit" description="What customers pay when they book.">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              control={control}
              name="bookingTerms"
              render={({ field }) => (
                <TextField
                  select
                  label="Deposit type"
                  value={field.value.depositType ?? ''}
                  onChange={(event) => {
                    const depositType = event.target.value as DepositType;
                    // As the tour form does: only a percentage or flat deposit has an amount, and no deposit has no due days.
                    field.onChange({
                      ...field.value,
                      depositType,
                      depositAmount:
                        depositType === 'percentage' || depositType === 'flat' ? field.value.depositAmount : null,
                      depositDueDays: depositType === 'noDeposit' ? null : field.value.depositDueDays,
                    });
                  }}
                  fullWidth
                >
                  {depositTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {DEPOSIT_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          {terms.depositType === 'percentage' && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Controller
                control={control}
                name="bookingTerms.depositAmount"
                render={({ field }) => (
                  <TextField
                    type="number"
                    label="Deposit"
                    value={field.value === null ? '' : Math.round(field.value * 10000) / 100}
                    onChange={(event) =>
                      field.onChange(event.target.value.trim() === '' ? null : Number(event.target.value) / 100)
                    }
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    inputProps={{ min: 1, max: 99, step: 1 }}
                    error={!!amountProblem}
                    helperText={amountProblem ?? 'Of the tour price'}
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}
          {terms.depositType === 'flat' && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <NativeTourNumberInput control={control} field="depositAmount" label="Deposit amount" />
            </Grid>
          )}
          {terms.depositType !== 'noDeposit' && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <NativeTourNumberInput
                control={control}
                field="depositDueDays"
                label="Final payment due days"
                helperText="Days before departure the balance is due"
              />
            </Grid>
          )}
        </Grid>
        <Box mt={2}>
          <NativeTourApplyDepositToBrand
            tourOfferId={tourOfferId}
            brandName={brandName}
            hasUnsavedDeposit={hasUnsavedBookingTerms}
          />
        </Box>
      </NativeTourCard>

      <NativeTourCard
        title="Payment schedule"
        description="The supplier's payment terms decide which svc-payment templates fit."
      >
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <NativeTourNumberInput
                control={control}
                field="finalPaymentDueDays"
                label="Supplier payment due days"
                helperText={`Days before departure the supplier is paid (${SUPPLIER_DUE_DAYS_DEFAULT} if unset)`}
              />
            </Grid>
          </Grid>
          <Controller
            control={control}
            name="bookingTerms.scheduleTemplateIds"
            render={({ field }) => (
              <Stack spacing={2}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={leadTime ? 'leadTime' : 'single'}
                  onChange={(_event, mode: 'single' | 'leadTime' | null) => {
                    if (mode) {
                      setPickedMode(mode);
                      field.onChange([]);
                    }
                  }}
                >
                  <ToggleButton value="single">One template</ToggleButton>
                  <ToggleButton value="leadTime">By lead time</ToggleButton>
                </ToggleButtonGroup>
                {leadTime ? (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    loading={isLoadingTemplates}
                    options={(templates ?? []).filter((template) => !!template.leadTimeRange).map(({ id }) => id)}
                    value={field.value}
                    onChange={(_event, ids) => field.onChange(ids)}
                    getOptionLabel={nameOf}
                    renderOption={(props, id) => {
                      const template = templates?.find((candidate) => candidate.id === id);
                      return (
                        <li {...props} key={id}>
                          <Stack>
                            <Typography>{nameOf(id)}</Typography>
                            {template && (
                              <Typography variant="caption" color="text.secondary">
                                {describeRange(template)}
                              </Typography>
                            )}
                          </Stack>
                        </li>
                      );
                    }}
                    renderTags={(ids, getTagProps) =>
                      ids.map((id, index) => (
                        <Chip {...getTagProps({ index })} key={id} size="small" label={nameOf(id)} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Templates by lead time"
                        error={!!problem}
                        helperText={problem ?? 'Customers get the template for how far ahead they book'}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    select
                    label="Template"
                    value={field.value[0] ?? ''}
                    onChange={(event) => field.onChange(event.target.value ? [event.target.value] : [])}
                    disabled={isLoadingTemplates}
                    helperText={templates?.find((template) => template.id === field.value[0])?.description}
                    fullWidth
                  >
                    <MenuItem value="">None</MenuItem>
                    {(templates ?? []).map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {field.value.some((id) => templates && !templates.some((template) => template.id === id)) && (
                  <Alert severity="warning">
                    A chosen template doesn't fit these supplier payment due days. Pick again from the list.
                  </Alert>
                )}
                <NativeTourTemplateDetails
                  templates={field.value.flatMap((id) => templates?.filter((template) => template.id === id) ?? [])}
                />
              </Stack>
            )}
          />
        </Stack>
      </NativeTourCard>

      <NativeTourCard title="From Salesforce" description="Read only until tours are set up here.">
        <NativeTourEntries
          entries={[
            {
              label: 'Date changes allowed up to (days before departure)',
              value: terms.dateChangeCutoffDays?.toString(),
            },
            {
              label: 'Optional experiences bookable up to (days before departure)',
              value: terms.optionalExperienceLeadDays?.toString(),
            },
          ]}
        />
      </NativeTourCard>
    </Stack>
  );
}
